import React, { useEffect, useMemo, useState } from "react";
import { api, apiErr } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { Button } from "@/components/ui/Button";
import { toast } from "sonner";
import {
  Loader2, FileText, X, ChevronRight, Download, Check, AlertCircle,
  Upload, Search, Clock, Calendar, Circle, Files, Folder, FolderOpen,
} from "lucide-react";
import { DOC_FOLDERS, FOLDER_LABEL, UNFILED } from "@/lib/docFolders";

const AVATAR_COLORS = [
  { bg: "#EEF2F7", fg: "#3d68b1" }, { bg: "#F4EFE9", fg: "#8B6E4A" },
  { bg: "#EEF3EF", fg: "#3F6B4E" }, { bg: "#F5EEF0", fg: "#A0546C" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
  if (isNaN(d)) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
}
function fmtBytes(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

const CONF_LABELS = {
  confidential: "Confidențial",
  professional: "Profesional",
};

export default function PersonalDocs() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/personal-docs/mine");
      setDocs(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return docs;
    const s = q.toLowerCase();
    return docs.filter(d =>
      (d.title || "").toLowerCase().includes(s) ||
      (d.type_info?.name || "").toLowerCase().includes(s));
  }, [docs, q]);

  const needsAttention = docs.filter(d => d.requires_read_receipt && !d.read_by_viewer);
  const expiringSoon = docs.filter(d => {
    if (!d.expiry_date) return false;
    const days = (new Date(d.expiry_date) - new Date()) / (1000 * 60 * 60 * 24);
    return days > 0 && days < 60;
  });

  return (
    <PortalLayout variant="staff">
      <div className="max-w-[1100px] mx-auto">
        <header className="pb-10 pt-2">
          <div className="text-[12px] font-display font-medium text-ink-muted mb-3">
            Profilul meu
          </div>
          <h1 className="text-[38px] md:text-[46px] leading-[1.05] font-display font-semibold tracking-[-0.02em] text-ink">
            Documentele mele
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed text-ink-muted max-w-xl">
            Contractul, adeverințele, instrucțiunile de lucru și alte documente asociate profilului tău.
          </p>
        </header>

        {needsAttention.length > 0 && (
          <div className="mb-4 rounded-2xl border border-amber-200/80 bg-amber-50/70 backdrop-blur px-5 py-4">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
                <AlertCircle className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[14px] font-semibold text-ink">
                  {needsAttention.length} document{needsAttention.length !== 1 ? "e" : ""} necesită confirmarea citirii
                </div>
                <div className="text-[13px] text-ink-muted mt-1">
                  Deschide fiecare document pentru a marca „Am citit și am înțeles&quot;.
                </div>
              </div>
            </div>
          </div>
        )}

        {expiringSoon.length > 0 && (
          <div className="mb-4 rounded-2xl border border-red-200/80 bg-red-50/70 backdrop-blur px-5 py-4">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center shrink-0">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[14px] font-semibold text-ink">
                  {expiringSoon.length} document{expiringSoon.length !== 1 ? "e" : ""} expiră în curând
                </div>
                <div className="text-[13px] text-ink-muted mt-1">
                  Contactează HR pentru reînnoirea documentelor.
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="py-4 mb-2 flex items-center gap-6">
          <div className="flex items-center gap-2.5 flex-1 min-w-0 sm:min-w-[280px] max-w-[420px] h-10 px-4 rounded-full border border-slate-200 bg-white/70 backdrop-blur focus-within:border-indigo-400 transition-colors">
            <Search className="h-4 w-4 text-slate-400" />
            <input value={q} onChange={e => setQ(e.target.value)}
              placeholder="Caută după titlu sau tip"
              style={{ boxShadow: "none" }}
              className="flex-1 bg-transparent outline-none border-0 text-[14px] placeholder:text-slate-400" />
          </div>
          <div className="text-[13px] text-slate-500 font-mono tabular-nums ml-auto whitespace-nowrap">
            {filtered.length} {filtered.length === 1 ? "document" : "documente"}
          </div>
        </div>

        <div className="surface overflow-hidden">
          {loading ? (
            <div className="py-24 flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-ink-muted" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-24 text-center text-ink-muted">
              <Files className="h-8 w-8 mx-auto text-neutral-300 mb-3" />
              <div className="text-[15px]">Nu ai documente încă.</div>
              <div className="text-[13px] mt-2">HR va încărca documente în profilul tău când vor fi disponibile.</div>
            </div>
          ) : (
            <div>
              {(() => {
                const byFolder = {};
                for (const d of filtered) {
                  const fid = (d.folder && FOLDER_LABEL[d.folder]) ? d.folder : UNFILED.id;
                  (byFolder[fid] = byFolder[fid] || []).push(d);
                }
                const groups = DOC_FOLDERS
                  .filter(f => (byFolder[f.id] || []).length)
                  .map(f => ({ id: f.id, label: f.label, docs: byFolder[f.id] }));
                if ((byFolder[UNFILED.id] || []).length)
                  groups.push({ id: UNFILED.id, label: UNFILED.label, docs: byFolder[UNFILED.id] });
                return groups.map(g => (
                  <div key={g.id} className="border-b border-indigo-50 last:border-0" data-testid={`emp-folder-${g.id}`}>
                    <div className="flex items-center gap-3 px-5 py-3 bg-indigo-50/40">
                      <FolderOpen className="h-4 w-4 text-amber-500" />
                      <span className="text-[13px] font-semibold text-ink flex-1">{g.label}</span>
                      <span className="text-[11px] font-mono text-slate-400">{g.docs.length}</span>
                    </div>
                    <div className="divide-y divide-indigo-50">
                      {g.docs.map(d => <DocRow key={d.id} doc={d} onClick={() => setSelected(d.id)} />)}
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      </div>

      <DocSheet open={!!selected} onClose={() => setSelected(null)}
        docId={selected} onChanged={load} />
    </PortalLayout>
  );
}


function DocRow({ doc, onClick }) {
  const needsRead = doc.requires_read_receipt && !doc.read_by_viewer;
  return (
    <button onClick={onClick}
      className="group w-full flex items-center gap-4 px-5 py-5 hover:bg-indigo-50/50 transition-colors text-left">
      <div className="h-11 w-11 rounded-full flex items-center justify-center bg-indigo-50 text-indigo-500 shrink-0">
        <FileText className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2.5 mb-1">
          <div className="text-[15px] font-medium text-ink truncate">{doc.title}</div>
          <span className="pio-badge pio-badge-gray font-mono">v{doc.version}</span>
          {needsRead && (
            <span className="pio-badge pio-badge-amber">
              <Circle className="h-1.5 w-1.5 fill-amber-500" /> Necesită confirmare
            </span>
          )}
          {doc.requires_read_receipt && doc.read_by_viewer && (
            <span className="pio-badge pio-badge-green">
              <Check className="h-3 w-3" /> Confirmat
            </span>
          )}
        </div>
        <div className="text-[13px] text-ink-muted truncate">
          {doc.type_info?.name} · {doc.issuer || "—"}
          {doc.document_number && ` · Nr. ${doc.document_number}`}
          {doc.expiry_date && ` · Expiră ${fmtDate(doc.expiry_date)}`}
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-0.5 transition-all shrink-0" />
    </button>
  );
}


function DocSheet({ open, onClose, docId, onChanged }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!open || !docId) return;
    (async () => {
      setLoading(true);
      try {
        // Load from user's own list (endpoint doesn't have get-by-id; fetch all and find)
        const { data } = await api.get("/personal-docs/mine");
        setDoc(data.find(d => d.id === docId));
      } catch (e) { toast.error(apiErr(e)); }
      finally { setLoading(false); }
    })();
  }, [open, docId]);

  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [open]);

  async function confirmRead() {
    setConfirming(true);
    try {
      await api.post(`/personal-docs/${docId}/confirm-read`);
      toast.success("Confirmarea a fost înregistrată");
      onChanged();
      onClose();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setConfirming(false); }
  }

  return (
    <>
      <div onClick={onClose}
        className={`fixed inset-0 bg-ink/20 transition-opacity duration-300 z-40 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`} />
      <aside className={`fixed top-0 right-0 bottom-0 z-50 w-full max-w-[720px] bg-white border-l border-neutral-200 flex flex-col transition-transform duration-300 ease-out ${
        open ? "translate-x-0" : "translate-x-full"
      }`}>
        {loading || !doc ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-ink-muted" />
          </div>
        ) : (
          <>
            <div className="px-8 pt-8 pb-6 border-b border-neutral-100">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-[12px] font-display font-medium text-ink-muted mb-2 flex items-center gap-3">
                    {doc.type_info?.name} · v{doc.version}
                  </div>
                  <div className="text-[22px] font-display font-semibold text-ink flex items-center gap-3">
                    <span className="h-9 w-9 rounded-full bg-gradient-to-br from-[#3b8ff0] to-[#9b6bf0] text-white flex items-center justify-center shrink-0 shadow-[0_8px_18px_-8px_rgba(96,110,240,0.65)]">
                      <FileText className="h-4 w-4" />
                    </span>
                    <span className="truncate">{doc.title}</span>
                  </div>
                </div>
                <button onClick={onClose}
                  className="h-10 w-10 flex items-center justify-center text-ink-muted hover:text-ink hover:bg-neutral-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-8 py-8 space-y-8">
              <section>
                <div className="text-[12px] font-display font-medium text-ink-muted mb-4">
                  Detalii
                </div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  <Meta label="Emitent" value={doc.issuer} />
                  <Meta label="Nr. document" value={doc.document_number} />
                  <Meta label="Data emiterii" value={fmtDate(doc.issue_date)} />
                  <Meta label="Data intrării în vigoare" value={fmtDate(doc.effective_date)} />
                  <Meta label="Data expirării" value={fmtDate(doc.expiry_date)} />
                  <Meta label="Confidențialitate" value={CONF_LABELS[doc.confidentiality] || "—"} />
                </div>
                {doc.notes && (
                  <div className="mt-6">
                    <div className="text-[12px] font-display font-medium text-ink-muted mb-1">
                      Note
                    </div>
                    <div className="text-[14px] text-ink">{doc.notes}</div>
                  </div>
                )}
              </section>

              <section>
                <div className="text-[12px] font-display font-medium text-ink-muted mb-4">
                  Versiuni ({doc.versions?.length || 0})
                </div>
                <div className="space-y-2">
                  {(doc.versions || []).slice().reverse().map(v => (
                    <a key={v.version}
                      href={`${backend}/api/personal-docs/${doc.id}/download?version=${v.version}`}
                      target="_blank" rel="noreferrer"
                      className="flex items-center justify-between p-3 border border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/40 rounded-xl transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="pio-badge pio-badge-gray font-mono tabular-nums">
                          v{v.version}
                        </div>
                        <div>
                          <div className="text-[14px] font-medium text-ink">{v.filename}</div>
                          <div className="text-[12px] text-ink-muted">
                            {fmtDate(v.uploaded_at)} · {fmtBytes(v.size)}
                          </div>
                        </div>
                      </div>
                      <Download className="h-4 w-4 text-ink-muted" />
                    </a>
                  ))}
                </div>
              </section>

              {doc.requires_read_receipt && (
                <section className="rounded-2xl border border-indigo-100 bg-indigo-50/60 p-6">
                  <div className="text-[12px] font-display font-medium text-ink-muted mb-3">
                    Confirmare citire
                  </div>
                  {doc.read_by_viewer ? (
                    <div className="flex items-center gap-2 text-[14px] text-emerald-700">
                      <Check className="h-4 w-4" />
                      Ai confirmat citirea acestei versiuni la {fmtDate(doc.read_at_viewer)}
                    </div>
                  ) : (
                    <div>
                      <p className="text-[14px] text-ink mb-4">
                        Prin apăsarea butonului de mai jos confirmi că ai citit și înțeles versiunea actuală a acestui document.
                      </p>
                      <Button variant="primary" size="md" icon={Check}
                        loading={confirming} onClick={confirmRead}>
                        Am citit și am înțeles
                      </Button>
                    </div>
                  )}
                </section>
              )}
            </div>
          </>
        )}
      </aside>
    </>
  );
}


function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[12px] font-display font-medium text-ink-muted mb-1">{label}</div>
      <div className="text-[14px] text-ink">{value || "—"}</div>
    </div>
  );
}
